import Container from '@mui/material/Container';
import { rootRoute } from '@rootScreen';
import { createRoute, redirect } from '@tanstack/react-router';
import Routes from '@constants/routes.enum';
import { Box, useColorScheme } from '@mui/material';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useState } from 'react';
import { gray } from '@configs/theme/theme';
import { getItem, setItem } from '@utils/localStorage';
import Line from '@components/ui/charts/Line';
import PieC from '@components/ui/charts/radial';
import { useAppDispatch, useAppSelector } from '@configs/store/hooks';
import * as shm from '@features/shm';
import { LuLoaderCircle } from 'react-icons/lu';

import moment from 'moment';

type inputs = {
  node: {
    [k in 'total' | 'active' | 'standby']: number;
  };
  investment: {
    [k in 'stake' | 'monthly_operations_cost' | 'start_date']: string | number;
  };
  rewards: {
    [k in 'rewards']: number;
  };
};
const Home = () => {
  const { mode, systemMode } = useColorScheme();
  const shmPrice = useAppSelector((state) => state?.shm?.price);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<inputs>({} as inputs);
  const [projectionData, setprojectionData] = useState<any[]>([]);
  const [cost, setCost] = useState<any[]>([]);
  const [calculations, setCalculations] = useState<
    ReturnType<typeof runCalculations>
  >({} as any);
  const node_inputs = [
    {
      name: 'Total Nodes',
      helperText: 'How many nodes are available?',
      key: 'total',
      placeholder: 'Enter total nodes',
      type: 'number',
    },
    {
      name: 'Active Nodes',
      helperText: 'How many nodes are you currently running?',
      key: 'active',
      placeholder: 'Enter total nodes',
      type: 'number',
    },
    {
      name: 'Standby Nodes',
      helperText: 'How many nodes are you currently on stand-by?',
      key: 'standby',
      placeholder: 'Enter total nodes',
      type: 'number',
    },
  ];
  const investment_inputs = [
    {
      name: 'SHM STAKE',
      helperText: 'How much in SHM did you stake?',
      key: 'stake',
      placeholder: 'Enter SHM Stake',
      type: 'number',
    },
    {
      name: 'Monthly Node Cost',
      helperText: 'How much do you spend on running all nodes?',
      key: 'monthly_operations_cost',
      placeholder: 'Enter total nodes',
      type: 'number',
    },
    {
      name: 'Start Date',
      helperText: 'When did you start running the nodes?',
      key: 'start_date',
      type: 'date',
    },
  ];
  const rewards_inputs = [
    {
      name: 'Reward per hour',
      helperText: 'How much in rewards (SHM) do you recieve per node?',
      key: 'rewards',
      placeholder: 'Enter SHM reward',
      type: 'number',
    },
  ];

  const handleValueChange = (newValue: string, key: string, group: string) => {
    setData((prev) => {
      return {
        ...prev,
        [group]: {
          ...prev[group],
          [key]: newValue,
        },
      };
    });
  };

  useEffect(() => {
    if (shmPrice) {
      const ls = getItem('home');
      if (ls && ls['node']) {
        setData(ls);
        setCalculations(runCalculations(ls));
        const _cost = costBreakdown(ls);
        setCost(_cost);
        const _data = pData(ls);
        setprojectionData(_data);
      }
    }
  }, [shmPrice]);

  const saveData = () => {
    setLoading(true);
    setItem('home', JSON.stringify(data));
    const _data = pData(data);
    const _cost = costBreakdown(data);
    setCalculations(runCalculations(data));
    setCost(_cost);
    setprojectionData(_data);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const runCalculations = (_data: inputs) => {
    const now = moment();
    const start = moment(_data.investment.start_date);
    const hours_to_date = Math.max(now.diff(start, 'hours'), 1);
    const months_to_date = Math.max(now.diff(start, 'months'), 1);

    console.log(hours_to_date);
    return {
      nodeUsdCost: Number(_data.investment?.stake) * shmPrice,
      monthlyRewardsShm:
        (Number(_data.rewards?.rewards) *
          hours_to_date *
          Number(_data.node?.total) *
          (Number(_data.node.standby) / Number(_data.node.active))) /
        months_to_date,
      monthlyRewardsUsd:
        ((Number(_data.rewards?.rewards) *
          hours_to_date *
          Number(_data.node?.total) *
          (Number(_data.node.standby) / Number(_data.node.active))) /
          months_to_date) *
        shmPrice,
      yearlyCosts: Number(_data.investment?.monthly_operations_cost) * 12,

      get yearlyRewardsUsd() {
        return this.monthlyRewardsUsd * 12;
      },

      get netYearlyReturn() {
        return this.yearlyRewardsUsd - this.yearlyCosts;
      },
      get totalInvestment() {
        return (
          Number(_data.node.total) * Number(_data.investment.stake) * shmPrice
        );
      },
      get apy() {
        return (this.netYearlyReturn / this.totalInvestment) * 100;
      },
      get roiMonths() {
        const monthlyProfit =
          this.monthlyRewardsUsd -
          Number(_data.investment?.monthly_operations_cost);
        return monthlyProfit > 0 ? this.totalInvestment / monthlyProfit : 0;
      },
      get monthlyProfit() {
        return (
          this.monthlyRewardsUsd -
          Number(_data.investment.monthly_operations_cost)
        );
      },
    };
  };

  const costBreakdown = (_data: inputs) => {
    let calculations = runCalculations(_data);
    console.log(_data, calculations);
    return [
      { name: 'Node Cost', value: calculations.nodeUsdCost, color: '#10B981' },
      {
        name: 'Annual Operating',
        value: calculations.yearlyCosts,
        color: '#F59E0B',
      },
      {
        name: 'SHM Stake',
        value:
          Number(_data.investment?.stake) * Number(_data.node.total) * shmPrice,
        color: '#3B82F6',
      },
    ];
  };

  // Chart data
  const pData = (_data) => {
    const calculations = runCalculations(_data);
    return Array.from({ length: 10 }, (_, i) => {
      const month = i + 1;
      const cumulativeRewards = calculations.monthlyRewardsUsd * month;
      const cumulativeCosts =
        Number(_data.investment?.monthly_operations_cost) * month;
      const netProfit =
        cumulativeRewards - cumulativeCosts - calculations.totalInvestment;

      return {
        month,
        rewards: cumulativeRewards,
        costs: cumulativeCosts,
        netProfit: Math.max(netProfit, -calculations.totalInvestment),
      };
    });
  };
  return (
    <Container
      className="m-0 px-2 !pl-2 overflow-y-scroll flex flex-col !h-full relative gap-2"
      maxWidth={false}
      sx={{ placeContent: 'start' }}
    >
      <div className="w-full h-auto min-h-[70dvh] px-1">
        <Box
          className="w-full h-auto rounded-xl"
          sx={{
            bgcolor:
              mode === 'dark' || (mode === 'system' && systemMode === 'dark')
                ? gray[800]
                : gray[100],
          }}
        >
          <div className="w-full h-full flex flex-col py-2 md:px-4 px-5">
            <div className="w-full mb-3 text-base md:text-lg font-semibold">
              Network Information
            </div>
            <div className="w-full h-full flex md:grid flex-col md:grid-cols-3 md:divider-1 gap-4">
              <div className="w-full h-auto flex-1 flex flex-col gap-2">
                <div className="w-full text-sm md:text-base font-semibold">
                  Nodes Config
                </div>
                {node_inputs.map((item) => {
                  return (
                    <div className="flex flex-col w-full h-auto">
                      <h4 className="text-base md:text-sm font-normal m-0 p-0">
                        {item.name}
                      </h4>
                      {item.helperText && (
                        <p className="text-xs font-light mt-1">
                          {item.helperText}
                        </p>
                      )}
                      <input
                        className="mt-3 outline-0 border rounded-xl placeholder:text-base text-base py-1.5 px-2"
                        placeholder={item.placeholder || 'Enter Text here '}
                        type={item.type}
                        value={data?.node?.[item.key]}
                        onChange={(e) =>
                          handleValueChange(e.target.value, item.key, 'node')
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="w-full h-auto flex-1 flex flex-col gap-2">
                <div className="w-full text-sm md:text-base font-semibold">
                  Investment Information
                </div>
                {investment_inputs.map((item) => {
                  return (
                    <div className="flex flex-col w-full h-auto">
                      <h4 className="text-base md:text-sm font-normal m-0 p-0">
                        {item.name}
                      </h4>
                      {item.helperText && (
                        <p className="text-xs font-light mt-1">
                          {item.helperText}
                        </p>
                      )}
                      <input
                        className={
                          'mt-3 outline-0 border rounded-xl placeholder:text-base text-base py-1.5 px-2 '
                        }
                        placeholder={item.placeholder || 'Enter Text here '}
                        type={item.type}
                        value={data?.investment?.[item.key]}
                        onChange={(e) =>
                          handleValueChange(
                            e.target.value,
                            item.key,
                            'investment'
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="w-full h-auto flex-1 flex flex-col gap-2">
                <div className="w-full text-sm md:text-base font-semibold">
                  Rewards Information
                </div>
                {rewards_inputs.map((item) => {
                  return (
                    <div className="flex flex-col w-full h-auto">
                      <h4 className="text-base md:text-sm font-normal m-0 p-0">
                        {item.name}
                      </h4>
                      {item.helperText && (
                        <p className="text-xs font-light mt-1">
                          {item.helperText}
                        </p>
                      )}
                      <input
                        className={
                          'mt-3 outline-0 border rounded-xl placeholder:text-base text-base py-1.5 px-2 '
                        }
                        placeholder={item.placeholder || 'Enter Text here '}
                        type={item.type}
                        value={data?.rewards?.[item.key]}
                        onChange={(e) =>
                          handleValueChange(e.target.value, item.key, 'rewards')
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              onClick={saveData}
              className="bg-primary rounded-lg px-5 py-3 text-sm font-semibold cursor-pointer w-full md:w-auto h-auto md:ml-auto mt-6 !text-white mb-3"
            >
              Submit
            </button>
          </div>
        </Box>
      </div>
      {calculations?.apy ? (
        <div className="w-full h-auto  px-1">
          <Box
            className="w-full h-auto rounded-xl"
            sx={{
              bgcolor:
                mode === 'dark' || (mode === 'system' && systemMode === 'dark')
                  ? gray[800]
                  : gray[100],
            }}
          >
            {!loading && (
              <div className="w-full min-h-[70dvh] flex flex-col md:grid grid-cols-2 grid-flow-row-dense gap-3 py-2 md:px-4 px-5">
                <div className=" rounded-2xl p-6 w-full h-auto col-span-2">
                  <h2 className="text-2xl font-bold mb-6 flex items-center">
                    <span className="bg-gradient-to-r from-green-400 to-emerald-400 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-slate-900 mr-3">
                      ✓
                    </span>
                    Results
                  </h2>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 p-4 rounded-xl border border-green-500/30">
                      <div className="text-green-300 text-sm font-medium">
                        Annual APY
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {calculations?.apy?.toFixed(2)}%
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 p-4 rounded-xl border border-blue-500/30">
                      <div className="text-blue-300 text-sm font-medium">
                        ROI Timeframe
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {calculations?.roiMonths?.toFixed(1)}mo
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4 rounded-xl border border-purple-500/30">
                      <div className="text-purple-300 text-sm font-medium">
                        Monthly Profit
                      </div>
                      <div className="text-2xl font-bold text-white">
                        ${calculations?.monthlyProfit?.toFixed(2)}
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-4 rounded-xl border border-orange-500/30">
                      <div className="text-orange-300 text-sm font-medium">
                        Total Investment
                      </div>
                      <div className="text-2xl font-bold text-white">
                        ${calculations?.totalInvestment?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-auto w-auto flex flex-col items-center">
                  <div className="w-full h-auto mb-3 py-2 text-base font-bold">
                    Earnings Projection{' '}
                  </div>
                  <div className="flex-1 w-full">
                    <Line data={projectionData} />
                  </div>
                </div>
                <div className="h-auto w-auto flex flex-col items-center">
                  <div className="w-full h-auto mb-3 py-2 text-base font-bold">
                    Investment Breakdown
                  </div>
                  <div className="w-full h-auto">
                    <PieC data={cost} />
                  </div>
                  <div className="space-y-2 w-full h-auto">
                    {cost.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <div
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className=" text-sm">{item.name}</span>
                        </div>
                        <span className="text-primary font-medium">
                          ${item.value.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <div className="w-full flex min-h-[40dvh] justify-center items-center">
                <LuLoaderCircle className="animate-spin text-3xl" />
              </div>
            )}
          </Box>
        </div>
      ) : (
        ''
      )}
      <div className="w-full h-auto  px-1">
        <Box
          className="w-full h-auto rounded-xl mt-2"
          sx={{
            bgcolor:
              mode === 'dark' || (mode === 'system' && systemMode === 'dark')
                ? gray[800]
                : gray[100],
          }}
        >
          <div className="w-full h-auto p-6">
            <h3 className="text-lg font-bold mb-4">
              📋 Assumptions & Disclaimers
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-blue-200">
              <div>
                <h4 className="font-semibold mb-2">
                  Calculation Assumptions:
                </h4>
                <ul className="space-y-1">
                  <li>• Rewards are consistently charged per hour for active nodes</li>
                  <li>• SHM price remains stable over timeframe</li>
                  <li>• Operating costs are fixed monthly</li>
                  <li>• Node uptime is 99%+ (minimal downtime)</li>
                  <li>• Network rewards don't change significantly</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  Important Notes:
                </h4>
                <ul className="space-y-1">
                  <li>• Cryptocurrency investments carry high risk</li>
                  <li>• Past performance doesn't guarantee future results</li>
                  <li>• Rewards may vary based on network conditions</li>
                  <li>• Consider tax implications in your jurisdiction</li>
                  <li>• This is for educational purposes only</li>
                </ul>
              </div>
            </div>
          </div>
        </Box>
      </div>
    </Container>
  );
};
// Index route
export const homeRoute = createRoute({
  component: Home,
  getParentRoute: () => rootRoute,
  path: Routes.HOME,
});

// Not Found route
export const notFoundRoute = createRoute({
  component: Home,
  getParentRoute: () => rootRoute,
  path: Routes.NOT_FOUND,
});

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({
      to: '/home',
    });
  },
});
export default Home;
